import React from 'react';
import { Row, Col, FormGroup, Label, Input, Card, CardHeader, CardBody, UncontrolledTooltip, Table, Button } from 'reactstrap';
import { commissionRate } from '../DashboardProject/BidPriceLogic';
import { CostBreakdownItem } from '../DashboardProject/BidShared';

interface PaymentScheduleItem {
  milestoneSequence?: number;
  condition: string;
  percentage: number;
  isLocked: boolean;
}

interface BidDetailsFormProps {
  bidType: string;
  setBidType: (value: string) => void;
  fixedPriceType: string;
  setFixedPriceType: (value: string) => void;
  bidAmount: string;
  bidAmountContractor: string;
  handleBidAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBidAmountContractorChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  approach: string;
  setApproach: (value: string) => void;
  requireInspection: boolean;
  setRequireInspection: (value: boolean) => void;
  warrantyPeriod: string;
  setWarrantyPeriod: (value: string) => void;
  costBreakdown: CostBreakdownItem[];
  handleCostBreakdownChange: (index: number, field: keyof CostBreakdownItem, value: string) => void;
  addCostBreakdownItem: () => void;
  removeCostBreakdownItem: (index: number) => void;
  isCostBreakdownValid: boolean;
  paymentSchedule: PaymentScheduleItem[];
  handlePaymentScheduleChange: (index: number, field: 'condition' | 'percentage', value: string | number) => void;
  addPaymentScheduleItem: () => void;
  removePaymentScheduleItem: (index: number) => void;
  validatePaymentSchedule: () => boolean;
  requireInspectionSetPaymentSchedule: () => void;
}

const BidDetailsForm: React.FC<BidDetailsFormProps> = ({
  bidType,
  setBidType,
  fixedPriceType,
  setFixedPriceType,
  bidAmount,
  bidAmountContractor,
  handleBidAmountChange,
  handleBidAmountContractorChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  approach,
  setApproach,
  requireInspection,
  setRequireInspection,
  warrantyPeriod,
  setWarrantyPeriod,
  costBreakdown,
  handleCostBreakdownChange,
  addCostBreakdownItem,
  removeCostBreakdownItem,
  isCostBreakdownValid,
  paymentSchedule,
  handlePaymentScheduleChange,
  addPaymentScheduleItem,
  removePaymentScheduleItem,
  validatePaymentSchedule,
  requireInspectionSetPaymentSchedule,
}) => {
  // Commission breakdown tooltip content
  const commissionBreakdownTooltip = (
    <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-line', textAlign: 'right' }}>
      {`Customer Acquisition: 4%
  Payment Processing: 3%
        Platform Fee: 3%
        ----------------
              Total: 10%`}
    </div>
  );

  return (
    <Card className="mt-4 mb-4">
      <CardHeader>
        <h5 className="mb-0">Bid Details</h5>
      </CardHeader>
      <CardBody>
        <FormGroup>
          <Label for="bidType">Bid Type</Label>
          <Input
            type="select"
            name="bidType"
            id="bidType"
            value={bidType}
            onChange={(e) => setBidType(e.target.value)}
          >
            <option value="fixedPrice">Fixed Price</option>
            <option value="hourly">Hourly</option>
          </Input>
        </FormGroup>
        
        {bidType === 'fixedPrice' && (
          <FormGroup>
            <Label for="fixedPriceType">Fixed Price Type</Label>
            <Input
              type="select"
              name="fixedPriceType"
              id="fixedPriceType"
              value={fixedPriceType}
              onChange={(e) => setFixedPriceType(e.target.value)}
            >
              <option value="laborAndMaterials">Labor & Materials</option>
              <option value="laborOnly">Labor Only</option>
            </Input>
          </FormGroup>
        )}

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="bidAmountContractor">Contractor Price ({bidType === 'fixedPrice' ? 'Total' : 'Hourly Rate'})</Label>
              <Input
                type="number"
                name="bidAmountContractor"
                id="bidAmountContractor"
                value={bidAmountContractor}
                onChange={handleBidAmountContractorChange}
                required
              />
              <UncontrolledTooltip
                target="bidAmountContractor"
                transition={{ timeout: 150 }}>
                <div>
                  <div>The price you'll receive. A {commissionRate * 100}% commission is added to this amount for the homeowner price.</div>
                  <br />
                  <div><strong>Commission Breakdown:</strong></div>
                  {commissionBreakdownTooltip}
                </div>
              </UncontrolledTooltip>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="bidAmount">Homeowner Price ({bidType === 'fixedPrice' ? 'Total' : 'Hourly Rate'})</Label>
              <Input
                type="number"
                name="bidAmount"
                id="bidAmount"
                value={bidAmount}
                onChange={handleBidAmountChange}
                required
              />
              <UncontrolledTooltip
                target="bidAmount"
                transition={{ timeout: 150 }}>
                <div>
                  <div>The price the homeowner will see. This includes a {commissionRate * 100}% commission.</div>
                  <br />
                  <div><strong>Commission Breakdown:</strong></div>
                  {commissionBreakdownTooltip}
                </div>
              </UncontrolledTooltip>
            </FormGroup>
          </Col>
        </Row>

        <h5>Cost Breakdown</h5>
        <div className="table-responsive">
          <Table className="cost-breakdown-table">
            <thead>
              <tr>
                <th className="description-col">Description</th>
                <th className="amount-col">Contractor Amount</th>
                <th className="amount-col">Homeowner Amount</th>
                <th className="action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {costBreakdown.map((item, index) => (
                <tr key={index}>
                  <td className="description-col">
                    <Input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleCostBreakdownChange(index, 'description', e.target.value)}
                    />
                  </td>
                  <td className="amount-col">
                    <Input
                      type="number"
                      value={item.contractorAmount}
                      onChange={(e) => handleCostBreakdownChange(index, 'contractorAmount', e.target.value)}
                    />
                  </td>
                  <td className="amount-col">
                    <Input
                      type="number"
                      value={item.homeownerAmount}
                      onChange={(e) => handleCostBreakdownChange(index, 'homeownerAmount', e.target.value)}
                    />
                  </td>
                  <td className="action-col">
                    <Button color="danger" className="btn-sm" onClick={() => removeCostBreakdownItem(index)}>
                      <span className="d-none d-md-inline">Remove</span>
                      <i className="ri-delete-bin-line d-inline d-md-none"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Button color="primary" size="sm" onClick={addCostBreakdownItem}>
          Add Cost Item
        </Button>
        {!isCostBreakdownValid && (
          <div className="text-danger mt-2">
            Cost breakdown total must equal bid amount
          </div>
        )}

        <Row className="mt-5">
          <Col md={6}>
            <div className="mb-3">
              <Label for="startDate">Estimated Start Date</Label>
              <Input
                type="date"
                name="startDate"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
              <UncontrolledTooltip
                target="startDate"
                transition={{ timeout: 150 }}>
                We recommend padding the Start Date to be the worst case scenario, as it will be specified in the contract as a guaranteed start date.
              </UncontrolledTooltip>
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label for="endDate">Estimated Completion Date</Label>
              <Input
                type="date"
                name="endDate"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
              <UncontrolledTooltip
                target="endDate"
                transition={{ timeout: 150 }}>
                We recommend padding the Completion Date to be the worst case scenario, as it will be specified in the contract as a guaranteed completion date.
              </UncontrolledTooltip>
            </div>
          </Col>
        </Row>

        <div className="mb-3">
          <Label for="approach">Approach Description</Label>
          <Input
            type="textarea"
            name="approach"
            id="approach"
            value={approach}
            onChange={(e) => setApproach(e.target.value)}
            required
            maxLength={500}
          />
        </div>

        <Row>
          <Col md={6}>
            <FormGroup className="mb-3">
              <div className="toggle-switch-wrapper">
                <Label for="requireInspectionToggle" className="d-block mb-2">
                  Require Inspection
                </Label>
                <div className="d-flex align-items-center">
                  <Label className="toggle-switch" for="requireInspectionToggle">
                    <Input
                      type="checkbox"
                      id="requireInspectionToggle"
                      checked={requireInspection}
                      onChange={() => {
                        setRequireInspection(!requireInspection);
                        requireInspectionSetPaymentSchedule();
                      }}
                    />
                    <span className="slider"></span>
                  </Label>
                </div>
              </div>
              <UncontrolledTooltip
                target="requireInspectionToggle"
                transition={{ timeout: 150 }}>
                Require property inspection. With a property inspection, the contract is contingent upon the inspection. When you do not require an inspection, a bid accepted by the homeowner will be immediately under contract.
              </UncontrolledTooltip>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="warrantyPeriod">Warranty Period (In Days)</Label>
              <Input
                type="number"
                name="warrantyPeriod"
                id="warrantyPeriod"
                value={warrantyPeriod}
                onChange={(e) => setWarrantyPeriod(e.target.value)}
                placeholder="365"
                min="0"
                required
              />
            </FormGroup>
          </Col>
        </Row>

        <h5 className="mt-5">Payment Schedule</h5>
        <Table>
          <thead>
            <tr>
              <th>Payment Condition</th>
              <th>Percentage</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paymentSchedule.map((item, index) => (
              <tr key={index}>
                <td>
                  <Input
                    type="text"
                    value={item.condition}
                    onChange={(e) => handlePaymentScheduleChange(index, 'condition', e.target.value)}
                    disabled={item.isLocked}
                  />
                </td>
                <td>
                  <Input
                    type="number"
                    value={item.percentage}
                    onChange={(e) => handlePaymentScheduleChange(index, 'percentage', e.target.value)}
                    disabled={item.isLocked}
                  />
                </td>
                <td>
                  {!item.isLocked && (
                    <Button color="danger" size="sm" onClick={() => removePaymentScheduleItem(index)}>
                      Remove
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button color="primary" size="sm" onClick={addPaymentScheduleItem}>
          Add Payment Milestone
        </Button>
        {!validatePaymentSchedule() && (
          <div className="text-danger mt-2">
            Total percentage must equal 100%
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default BidDetailsForm;